import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import ConfiguratorLayout from '@/components/configurator/ConfiguratorLayout'
import Step1Role from '@/components/configurator/steps/Step1Role'
import Step2Environment from '@/components/configurator/steps/Step2Environment'
import Step3Missions from '@/components/configurator/steps/Step3Missions'
import Step4AI from '@/components/configurator/steps/Step4AI'
import Step5Physical from '@/components/configurator/steps/Step5Physical'
import Step6Appearance from '@/components/configurator/steps/Step6Appearance'
import Step7Autonomy from '@/components/configurator/steps/Step7Autonomy'
import Step8Budget from '@/components/configurator/steps/Step8Budget'
import Step9Auth from '@/components/configurator/steps/Step9Auth'
import Step10Result from '@/components/configurator/steps/Step10Result'
import { useWizardStore } from '@/store/wizardStore'
import { useLang } from '@/hooks/useLang'
import { useAudio } from '@/hooks/useAudio'
import type { Lang } from '@/hooks/useLang'

interface StepMeta {
  fr: { title: string; phase: string }
  en: { title: string; phase: string }
}

const STEP_META: Record<number, StepMeta> = {
  1: {
    fr: { title: 'Étape 1 – Quel est le rôle principal de votre robot ?', phase: 'Phase d\'Initialisation' },
    en: { title: 'Step 1 – What is the primary role of your robot?', phase: 'Initialisation Phase' },
  },
  2: {
    fr: { title: 'Étape 2 – Dans quel environnement opérera-t-il ?', phase: 'Définition du Contexte' },
    en: { title: 'Step 2 – In what environment will it operate?', phase: 'Context Definition' },
  },
  3: {
    fr: { title: 'Étape 3 – Quelles sont les missions prioritaires ?', phase: 'Définition des Missions' },
    en: { title: 'Step 3 – What are the priority missions?', phase: 'Mission Definition' },
  },
  4: {
    fr: { title: 'Étape 4 – Quel niveau d\'autonomie désirez-vous ?', phase: 'Configuration IA' },
    en: { title: 'Step 4 – What level of autonomy do you want?', phase: 'AI Configuration' },
  },
  5: {
    fr: { title: 'Étape 5 – Spécifications physiques', phase: 'Configuration Matérielle' },
    en: { title: 'Step 5 – Physical specifications', phase: 'Hardware Configuration' },
  },
  6: {
    fr: { title: 'Étape 6 – Apparence et identité visuelle', phase: 'Design & Identité' },
    en: { title: 'Step 6 – Appearance and visual identity', phase: 'Design & Identity' },
  },
  7: {
    fr: { title: 'Étape 7 – Autonomie et gestion de l\'énergie', phase: 'Gestion Énergétique' },
    en: { title: 'Step 7 – Autonomy and energy management', phase: 'Energy Management' },
  },
  8: {
    fr: { title: 'Étape 8 – Enveloppe budgétaire', phase: 'Configuration Financière' },
    en: { title: 'Step 8 – Budget envelope', phase: 'Financial Configuration' },
  },
  9: {
    fr: { title: 'Étape 9 – Sauvegarde de votre configuration', phase: 'Authentification' },
    en: { title: 'Step 9 – Save your configuration', phase: 'Authentication' },
  },
  10: {
    fr: { title: 'Étape 10 – Récapitulatif & Résultats', phase: 'Finalisation' },
    en: { title: 'Step 10 – Summary & Results', phase: 'Finalisation' },
  },
}

function StepContent({ step, lang }: { step: number; lang: Lang }) {
  switch (step) {
    case 1: return <Step1Role lang={lang} />
    case 2: return <Step2Environment lang={lang} />
    case 3: return <Step3Missions lang={lang} />
    case 4: return <Step4AI lang={lang} />
    case 5: return <Step5Physical lang={lang} />
    case 6: return <Step6Appearance lang={lang} />
    case 7: return <Step7Autonomy lang={lang} />
    case 8: return <Step8Budget lang={lang} />
    case 9: return <Step9Auth lang={lang} />
    case 10: return <Step10Result lang={lang} />
    default: return null
  }
}

export default function ConfiguratorPage() {
  const { lang, toggle: toggleLang } = useLang()
  const { enabled: audioEnabled, playing: audioPlaying, toggle: toggleAudio } = useAudio(lang)
  const { currentStep } = useWizardStore()

  const meta = STEP_META[currentStep]?.[lang] ?? STEP_META[1][lang]

  return (
    <div className="min-h-screen bg-surface text-on-surface overflow-x-hidden selection:bg-primary-container selection:text-on-primary-container">
      <Header
        lang={lang}
        audioEnabled={audioEnabled}
        audioPlaying={audioPlaying}
        onToggleAudio={toggleAudio}
        onToggleLang={toggleLang}
      />

      <main className="relative pt-20">
        <ConfiguratorLayout lang={lang} stepTitle={meta.title} phaseLabel={meta.phase}>
          <StepContent step={currentStep} lang={lang} />
        </ConfiguratorLayout>
      </main>

      <Footer lang={lang} />
    </div>
  )
}
